import React from 'react';
import { isSSR } from './ssr';

export interface UseCaseInsensitiveQueryParamConfig<T> {
  encode: (value: T) => string;
  decode: (value: string) => T;
}

export const useCaseInsensitiveQueryParam = <T>(
  queryParam: string,
  config: UseCaseInsensitiveQueryParamConfig<T>,
  defaultValue?: T,
): [T | undefined, (value: T | undefined) => void] => {
  const getDecodedValue = React.useCallback(() => {
    if (isSSR) {
      return undefined;
    }
    const searchParams = new URLSearchParams(window.location.search);
    const value = searchParams.get(queryParam);
    return value ? config.decode(value) : undefined;
  }, [queryParam, config]);

  const [decodedValue, setDecodedValue] = React.useState<T | undefined>(getDecodedValue() ?? defaultValue);

  const removeFromURL = React.useCallback(() => {
    if (isSSR) {
      return;
    }
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.delete(queryParam);
    window.history.replaceState({}, '', `${window.location.pathname}?${searchParams}`);
    setDecodedValue(getDecodedValue());
  }, [queryParam, config]);

  // On initial load, search for duplicates in the URL in various cases and remove them //
  React.useEffect(() => {
    if (isSSR) {
      return;
    }
    const searchParams = new URLSearchParams(window.location.search);
    const value = searchParams.get(queryParam);

    if (value) {
      const allDuplicateFiltersInURL: [string, string][] = [];
      searchParams.forEach((val, key) => {
        if (key.toLowerCase() === queryParam.toLowerCase()) {
          allDuplicateFiltersInURL.push([key, val]);
        }
      });

      if (allDuplicateFiltersInURL.length > 1) {
        allDuplicateFiltersInURL.forEach(([key]) => {
          searchParams.delete(key);
        });

        searchParams.set(queryParam, value);
        window.history.replaceState({}, '', `${window.location.pathname}?${searchParams}`);
        setDecodedValue(getDecodedValue());
      }
    }
  }, [queryParam]);

  const updateCallback = React.useCallback(
    (param: T | undefined) => {
      if (param === undefined) {
        removeFromURL();
        return;
      }

      const searchParams = new URLSearchParams(window.location.search);
      searchParams.set(queryParam, config.encode(param));
      window.history.replaceState({}, '', `${window.location.pathname}?${searchParams}`);

      setDecodedValue(getDecodedValue());
    },
    [queryParam, config],
  );

  return [decodedValue, updateCallback];
};

export const CaseInsensitiveStringParam: UseCaseInsensitiveQueryParamConfig<string> = {
  encode: (value) => encodeURIComponent(value),
  decode: (value) => decodeURIComponent(value),
};

export const CaseInsensitiveNumberParam: UseCaseInsensitiveQueryParamConfig<number> = {
  encode: (value) => encodeURIComponent(value),
  decode: (value) => Number.parseInt(decodeURIComponent(value)),
};
